// src/pages/AboutPage.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { FloatingContactContext } from '../components/FloatingContact';
import '../styles/AboutPage.css';

const AboutPage = () => {
  const { handleOpenContact } = useContext(FloatingContactContext);

  // S3 image URL for the "Why We Exist" section
  const aboutImageUrl = "https://legate-images.s3.us-west-2.amazonaws.com/images/about/bright+gold+logo.png";

  return (
    <div className="about-page">
      {/* About Header */}
      <section className="about-header">
        <div className="container">
          <h1 className="about-title">Our Mission</h1>
          <p className="about-mission">
            We help entrepreneurs launch and protect their businesses 
            with accessible, reliable support.
          </p>
        </div>
      </section>
      
      {/* Our Purpose Section */}
      <section className="section purpose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why We Exist</h2>
            <div className="title-underline"></div>
          </div>
          <div className="purpose-content">
            <div className="purpose-text">
              <p>
                Legate was created with a simple belief: entrepreneurship should be accessible to everyone.
                Too often, the legal aspects of starting a business become barriers rather than foundations
                for success.
              </p>
              <p>
                Our mission is to demystify business formation and compliance, providing hands-on
                support to entrepreneurs at every stage of their journey. We believe in empowerment
                through knowledge, clarity, and personalized guidance.
              </p>
            </div>
            <div className="purpose-image">
              <img 
                src={aboutImageUrl} 
                alt="Our Purpose - Supporting Entrepreneurs" 
                className="purpose-img"
                onError={(e) => {
                  // If the image fails to load, show the placeholder
                  e.target.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.className = 'placeholder-image';
                  e.target.parentNode.appendChild(placeholder);
                }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* How We're Different Section */}
      <section className="section difference-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How We're Different</h2>
            <div className="title-underline"></div>
          </div>
          <div className="difference-content">
            <div className="difference-item">
              <h3>Real People, Real Support</h3>
              <p>
                We're not an automated platform. Every client works with our 
                team of real people providing personalized guidance throughout the process.
              </p>
            </div>
            
            <div className="difference-item">
              <h3>Compliance-Focused</h3>
              <p>
                We don't just file paperwork—we ensure your business meets all
                legal requirements and stays compliant as regulations change.
              </p>
            </div>
            
            <div className="difference-item">
              <h3>No Jargon Zone</h3>
              <p>
                We translate complex legal concepts into clear, actionable information
                you can understand and use to make confident decisions.
              </p>
            </div>
            
            <div className="difference-item">
              <h3>Long-Term Partners</h3>
              <p>
                Our relationship doesn't end when your business is formed. We're here
                to support your compliance needs as your business grows.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="section values-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Core Values</h2>
            <div className="title-underline"></div>
          </div>
          <div className="values-content">
            <div className="value-item">
              <div className="value-icon">
                <i className="fas fa-balance-scale"></i>
              </div>
              <h3>Integrity</h3>
              <p>
                We provide honest guidance, even when it's not what's most profitable.
                Your success is our priority.
              </p>
            </div>
            
            <div className="value-item">
              <div className="value-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <h3>Clarity</h3>
              <p>
                We make the complex simple, transforming legal jargon into
                accessible information you can use.
              </p>
            </div>
            
            <div className="value-item">
              <div className="value-icon">
                <i className="fas fa-fist-raised"></i>
              </div>
              <h3>Empowerment</h3>
              <p>
                We equip entrepreneurs with the knowledge and support they
                need to build with confidence.
              </p>
            </div>
            
            <div className="value-item">
              <div className="value-icon">
                <i className="fas fa-universal-access"></i>
              </div>
              <h3>Accessibility</h3>
              <p>
                We make professional business formation and compliance
                support available to everyone.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Business Journey?</h2>
            <p className="cta-text">
              Let us help you build a solid foundation for your business success.
            </p>
            <div className="cta-buttons">
              <Link to="/service-form" className="btn btn-secondary">Start Your Business</Link>
              <button onClick={handleOpenContact} className="btn btn-outline-light">Free Consultation</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
